import {
  DueDiligenceRecordStatus,
  DueDiligenceRecordTierStatus,
  FatSeekingInterestPageInvestmentQuery,
  StrategyDetailsPageGetDueDiligenceRecordQuery
} from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { FlagIcon } from '@/assets/icons/info-icons/flag';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import Button from '@/components/basicComponents/button';
import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import ShowText from '@/components/dueDiligence-page/dueDiligenceRecord/showText';
import { Loader } from '@/components/loaders/loader';
import { useResponsive } from '@/hooks/use-responsive';
import { PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloError, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import DeleteDueDiligenceRecord from './components/modals/deleteDueDiligenceRecord';
import { ResearchTabZeroState } from './components/researchTabZeroState';
import { STRATEGY_DETAILS_PAGE_GET_DUE_DILIGENCE_RECORD_QUERY } from './queries';
import { StrategyDetailsPageDueDiligenceRecordSummary } from './types';

const getScoreStyles = (score: number) => {
  if (score === 0) {
    return {
      backgroundColor: themeVar('context.light'),
      color: themeVar('font.strong')
    };
  }
  if (score > 0) {
    return {
      backgroundColor: themeVar('context.success'),
      color: themeVar('context.light')
    };
  }
  if (score < 0) {
    return {
      backgroundColor: themeVar('context.error'),
      color: themeVar('context.light')
    };
  }
};

const getDataByRecordStatus = ({
  status,
  approver,
  statusUpdatedAt
}: {
  status: DueDiligenceRecordStatus;
  approver: StrategyDetailsPageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['approver'];
  statusUpdatedAt: string;
}) => {
  const completedBy = `${approver?.firstName} ${approver?.lastName}`;
  const completedAt = statusUpdatedAt ? format(new Date(statusUpdatedAt), 'LLL d, yyyy') : '';

  switch (status) {
    case DueDiligenceRecordStatus.Active:
      return {
        icon: <FlagIcon fill={themeVar('font.weak')} />,
        title: 'Due Diligence Active'
      };
    case DueDiligenceRecordStatus.Approved:
      return {
        icon: <ApprovedIcon fill={themeVar('context.success')} />,
        title: `Due Diligence Approved By ${completedBy} on ${completedAt}`
      };
    case DueDiligenceRecordStatus.Rejected:
      return {
        icon: <RejectedBannerIcon fill={themeVar('context.error')} />,
        title: `Due Diligence Rejected By ${completedBy} on ${completedAt}`
      };
    default:
      return {
        icon: <FlagIcon fill={themeVar('font.weak')} />,
        title: 'Due Diligence Not Started'
      };
  }
};

const getTierStatus = (tierData: StrategyDetailsPageDueDiligenceRecordSummary['formattedTiersData'][number]) => {
  const { status, approver, statusUpdatedAt, completedTasksCount, tasksCount } = tierData || {};
  const completedBy = `${approver?.firstName} ${approver?.lastName}`;
  const completedAt = statusUpdatedAt ? format(new Date(statusUpdatedAt), 'LLL d, yyyy') : '';

  switch (status) {
    case DueDiligenceRecordTierStatus.NotStarted:
      return <>Not Started</>;
    case DueDiligenceRecordTierStatus.InProgress:
      return (
        <InProgressStatusWrapper>
          In Progress: {tasksCount - completedTasksCount}/{tasksCount} Tasks Remaining
        </InProgressStatusWrapper>
      );
    case DueDiligenceRecordTierStatus.Completed:
      return (
        <>
          Completed By {completedBy} on {completedAt}
        </>
      );
    case DueDiligenceRecordTierStatus.Rejected:
      return (
        <>
          Rejected By {completedBy} on {completedAt}
        </>
      );
    default:
      return <></>;
  }
};

interface ResearchTabProps {
  investmentData: FatSeekingInterestPageInvestmentQuery['Investment'] | null;
}

export const ResearchTab = ({ investmentData }: ResearchTabProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const [showZeroState, setShowZeroState] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [dueDiligenceRecordData, setDueDiligenceRecordData] = useState<StrategyDetailsPageDueDiligenceRecordSummary | null>(null);

  const { loading: dueDiligenceRecordLoading, refetch: dueDiligenceRecordRefetch } = useQuery(STRATEGY_DETAILS_PAGE_GET_DUE_DILIGENCE_RECORD_QUERY, {
    variables: {
      id: '',
      strategyId: params?.strategyId
    },
    onCompleted: (data) => {
      setShowZeroState(false);

      const { id, status, owner, approver, createdAt, statusUpdatedAt, note, funnel } = data?.getDueDiligenceRecord || {};
      const templateName = funnel?.docxTemplateStorageKey?.split('/')?.pop() || '';
      const totalTiersScore = funnel?.tiers?.reduce((accumulator, currentValue) => {
        return accumulator + currentValue.sections.reduce((accumulator, currentValue) => accumulator + currentValue.data.score, 0);
      }, 0);

      const formattedTiersData = funnel?.tiers?.map((tier) => {
        const { status, approver, statusUpdatedAt } = tier.data || {};
        const shouldShowScore = tier.sections.some((section) => section.userCanScore);
        const sectionsScore = tier.sections.reduce((accumulator, currentValue) => accumulator + currentValue.data.score, 0);
        const completedTasksCount = tier.sections.filter((section) => section.data.completed).length;
        return {
          id: tier.id,
          name: tier.name,
          status,
          approver,
          statusUpdatedAt,
          completedTasksCount,
          tasksCount: tier.tasksCount,
          sectionsScore,
          shouldShowScore
        };
      });

      const shouldShowTotalScore = formattedTiersData.some((tier) => tier.shouldShowScore);
      const { icon, title } = getDataByRecordStatus({ status, approver, statusUpdatedAt });

      setDueDiligenceRecordData({
        id,
        status,
        approver,
        owner,
        createdAt,
        statusUpdatedAt,
        note,
        templateName,
        funnelName: funnel?.name,
        totalTiersScore,
        shouldShowTotalScore,
        formattedTiersData,
        sectionIcon: icon,
        sectionTitle: title
      });
    },
    onError: (error) => {
      const err = error as ApolloError;
      if (err.message === 'Not Found') {
        setShowZeroState(true);
      }
    }
  });

  if (dueDiligenceRecordLoading) {
    return <Loader />;
  }

  if (showZeroState) {
    return (
      <CustomPaddingWrap>
        <ResearchTabZeroState investmentData={investmentData} dueDiligenceRecordRefetch={dueDiligenceRecordRefetch} />
      </CustomPaddingWrap>
    );
  }

  const { backgroundColor: totalScoreBackgroundColor, color: totalScoreColor } = getScoreStyles(dueDiligenceRecordData?.totalTiersScore || 0);

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'delete-due-diligence-record' && (
        <DeleteDueDiligenceRecord
          isOpen={modalWindow.isOpen}
          recordId={dueDiligenceRecordData?.id}
          dueDiligenceRecordRefetch={dueDiligenceRecordRefetch}
          onClose={() => setModalWindow({ isOpen: false, type: '' })}
        />
      )}
      <CustomPaddingWrap>
        <DueDiligenceSection>
          <DueDiligenceSectionHeader>
            <DueDiligenceSectionTitle>
              <FlagIconWrapper>{dueDiligenceRecordData?.sectionIcon}</FlagIconWrapper>
              <p>{dueDiligenceRecordData?.sectionTitle}</p>
              {dueDiligenceRecordData?.shouldShowTotalScore && (
                <TotalScoreWrapper>
                  Total Score
                  <ScoreContainer backgroundColor={totalScoreBackgroundColor} color={totalScoreColor}>
                    {dueDiligenceRecordData?.totalTiersScore > 0
                      ? `+${dueDiligenceRecordData?.totalTiersScore}`
                      : dueDiligenceRecordData?.totalTiersScore}
                  </ScoreContainer>
                </TotalScoreWrapper>
              )}
            </DueDiligenceSectionTitle>
            <DueDiligenceSectionHeaderTags isMobile={isMobile}>
              {dueDiligenceRecordData?.createdAt && <p>Started: {format(new Date(dueDiligenceRecordData?.createdAt), 'MMM d, yyyy')}</p>}
              {dueDiligenceRecordData?.owner && (
                <p>Owner: {`${dueDiligenceRecordData?.owner.firstName} ${dueDiligenceRecordData?.owner.lastName}`}</p>
              )}
              {dueDiligenceRecordData?.funnelName && (
                <TemplateNameWrapper isMobile={isMobile}>
                  <EllipsisWithTooltip title={dueDiligenceRecordData?.funnelName}>
                    <p>Funnel: {dueDiligenceRecordData?.funnelName}</p>
                  </EllipsisWithTooltip>
                </TemplateNameWrapper>
              )}
            </DueDiligenceSectionHeaderTags>
          </DueDiligenceSectionHeader>
          {dueDiligenceRecordData?.status === DueDiligenceRecordStatus.Rejected && dueDiligenceRecordData?.note && (
            <DueDiligenceSectionNote borderLeftColor={themeVar('context.error')}>
              <DueDiligenceSectionSubTitle>Rejection Notes</DueDiligenceSectionSubTitle>
              <ShowText>
                <DueDiligenceSectionNoteText>{dueDiligenceRecordData?.note}</DueDiligenceSectionNoteText>
              </ShowText>
            </DueDiligenceSectionNote>
          )}
          {dueDiligenceRecordData?.status === DueDiligenceRecordStatus.Approved && dueDiligenceRecordData?.note && (
            <DueDiligenceSectionNote borderLeftColor={themeVar('context.success')}>
              <DueDiligenceSectionSubTitle>Approval Notes</DueDiligenceSectionSubTitle>
              <ShowText>
                <DueDiligenceSectionNoteText>{dueDiligenceRecordData?.note}</DueDiligenceSectionNoteText>
              </ShowText>
            </DueDiligenceSectionNote>
          )}
          <DueDiligenceSectionTiers>
            <DueDiligenceSectionSubTitle>Tier Status</DueDiligenceSectionSubTitle>
            {dueDiligenceRecordData?.formattedTiersData.map((item, index) => {
              const { backgroundColor, color } = getScoreStyles(item.sectionsScore);
              return (
                <DueDiligenceSectionTierWrapper key={item.id} isFirstItem={index === 0}>
                  <DueDiligenceSectionTierValue>{item.name}</DueDiligenceSectionTierValue>
                  <DueDiligenceSectionTierValue>{getTierStatus(item)}</DueDiligenceSectionTierValue>
                  {item.shouldShowScore && (
                    <ScoreWrapper>
                      Score
                      <ScoreContainer backgroundColor={backgroundColor} color={color}>
                        {item.sectionsScore}
                      </ScoreContainer>
                    </ScoreWrapper>
                  )}
                </DueDiligenceSectionTierWrapper>
              );
            })}
          </DueDiligenceSectionTiers>
          <ButtonsWrapper>
            <Button size="md" onClick={() => navigate(`/dueDiligence/${dueDiligenceRecordData?.id}?backToTitle=Strategy Details`)}>
              View Due Diligence
            </Button>
            <Button styleType="outline" size="md" onClick={() => setModalWindow({ isOpen: true, type: 'delete-due-diligence-record' })}>
              Delete
            </Button>
          </ButtonsWrapper>
        </DueDiligenceSection>
      </CustomPaddingWrap>
    </>
  );
};

const CustomPaddingWrap = styled(PaddingWrap)`
  padding-top: 40px;
  padding-bottom: 40px;
`;

const DueDiligenceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 40px;
  border-radius: 10px;
  background: ${themeVar('layer.1')};
`;

const DueDiligenceSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  padding: 0 20px 0 50px;
`;

const FlagIconWrapper = styled.div`
  position: absolute;
  top: 7px;
  left: 0;
`;

const DueDiligenceSectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 23px;
  font-weight: 700;
  line-height: 24px;
  color: ${themeVar('font.base')};
`;

const DueDiligenceSectionHeaderTags = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '10px' : '30px')};
  font-size: 16px;
  font-weight: 600;
  line-height: 22.4px;
  color: ${themeVar('font.weak')};
`;

const DueDiligenceSectionTiers = styled.div`
  padding: 20px 20px 20px 50px;
`;

const TotalScoreWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 18.2px;
  padding: 10px 20px;
  color: ${themeVar('font.weak')};
`;

const TemplateNameWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '40%')};
  min-width: ${({ isMobile }) => (isMobile ? 'auto' : '250px')};
`;

const DueDiligenceSectionNote = styled.div<{ borderLeftColor: string }>`
  margin: 20px 20px 20px 25px;
  padding: 0 20px;
  border-left: 5px solid ${({ borderLeftColor }) => borderLeftColor};
`;

const DueDiligenceSectionNoteText = styled.p`
  font-size: 20px;
  font-weight: 400;
  line-height: 30px;
  color: ${themeVar('font.base')};
`;

const DueDiligenceSectionSubTitle = styled.p`
  font-size: 23px;
  font-weight: 700;
  line-height: 22.4px;
  padding-bottom: 20px;
  color: ${themeVar('font.strong')};
`;

const DueDiligenceSectionTierWrapper = styled.div<{ isFirstItem: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 200px;
  gap: 10px;
  padding: 10px 0;
  border-top: ${({ isFirstItem }) => (isFirstItem ? `1px solid ${themeVar('border.base')}` : 'none')};
  border-bottom: 1px solid ${themeVar('border.base')};
`;

const DueDiligenceSectionTierValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 10px 0;
`;

const InProgressStatusWrapper = styled.div`
  color: ${themeVar('context.warning')};
`;

const ScoreWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 18.2px;
  padding: 10px 20px;
  color: ${themeVar('font.weak')};
  margin-left: auto;
`;

const ScoreContainer = styled.div<{ backgroundColor: string; color: string }>`
  font-size: 20px;
  font-weight: 700;
  line-height: 22.4px;
  border-radius: 14px;
  padding: 2px 15px;
  color: ${({ color }) => color};
  background: ${({ backgroundColor }) => backgroundColor};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 20px 0;
`;
